<template>
  <div data-page-type="no-header">
    <Form as="div" ref="form">
      <page-spinner :spinner-url-list="spinnerUrlList" />
      <div id="travelLetter" data-page-type="flow" data-test-component="travelLetter">
        <div class="container">
          <header class="header-static">
            <a
              class="close-button open-modal"
              :href="vueModel.ContentModel.CloseButtonUrl"
              data-test="travel-letter-button-close"
            >
              <span class="icon icon-close" />
            </a>
          </header>
        </div>
        <div v-cloak id="mainView" class="no-footer row container">
          <div class="medium-12 large-6 columns">
            <section v-if="!showReceipt">
              <h1 data-test="travel-letter-headline" v-html="vueModel.ContentModel.Headline" />
              <article class="flow-group">
                <div class="control">
                  <h3 data-test="travel-letter-title" v-html="vueModel.ContentModel.Title" />
                  <div
                    data-test="travel-letter-description"
                    v-html="vueModel.ContentModel.Description"
                  />
                </div>

                <div class="control push-up">
                  <h-3-and-tooltip
                    data-test="travel-letter-label-destination"
                    :h3="vueModel.ContentModel.WhichCountryAreYouTravelingToLabel"
                    :tooltip="vueModel.ContentModel.WhichCountryAreYouTravelingToToolTip"
                  />
                  <div class="row">
                    <div class="large-6 medium-12 columns">
                      <Field
                        name="Destination"
                        v-slot="{ field }"
                        v-model="vueModel.TemplateModel.Country"
                        :label="vueModel.ContentModel.WhichCountryAreYouTravelingToFieldName"
                        rules="required"
                      >
                        <VueSelect
                          :options="countryList"
                          :reduce="(country) => country.Name"
                          v-bind="field"
                          autocomplete="country-select-off"
                          name="Destination"
                          :placeholder="
                            vueModel.ContentModel.WhichCountryAreYouTravelingToPlaceholderSelect
                          "
                          label="Name"
                          data-test="travel-letter-select-destination"
                        />
                        <ValidationError
                          :name="field.name"
                          data-test="travel-letter-validation-destination"
                        />
                      </Field>
                    </div>
                  </div>
                </div>
                <div class="control push-up">
                  <h-3-and-tooltip
                    data-test="travel-letter-label-departure"
                    :h3="vueModel.ContentModel.DepartureLabel"
                    :tooltip="vueModel.ContentModel.DepartureTooltip"
                  />
                  <div class="row">
                    <div class="large-6 medium-12 columns">
                      <Field
                        name="DepartureDate"
                        v-slot="{ field }"
                        v-model="vueModel.TemplateModel.FromDate"
                        :rules="'required'"
                        :label="vueModel.ContentModel.DepartureFieldName"
                      >
                        <date-picker
                          id="departuredate"
                          :date="field.value"
                          v-bind="field"
                          name="DepartureDate"
                          class="datepicker"
                          :min-date="today"
                          :placeholder="vueModel.ContentModel.DatePlaceHolder"
                          data-test="travel-letter-date-departure"
                        />
                      </Field>
                      <ValidationError
                        name="DepartureDate"
                        data-test="travel-letter-validation-departure"
                      />
                    </div>
                  </div>
                </div>

                <div class="control push-up">
                  <h-3-and-tooltip
                    data-test="travel-letter-label-return"
                    :h3="vueModel.ContentModel.HomecomingLabel"
                    :tooltip="vueModel.ContentModel.HomecommingTooltip"
                  />
                  <div class="row">
                    <div class="large-6 medium-12 columns">
                      <Field
                        name="ReturnDate"
                        v-slot="{ field }"
                        v-model="vueModel.TemplateModel.ToDate"
                        :rules="
                          'required|min_date:' +
                          earliestReturnDateFormatted +
                          '|max_date:' +
                          latestReturnDateFormatted
                        "
                        :label="vueModel.ContentModel.HomecomingFieldName"
                      >
                        <date-picker
                          id="returndate"
                          v-bind="field"
                          :date="field.value"
                          class="datepicker"
                          :min-date="earliestReturnDate"
                          :placeholder="vueModel.ContentModel.DatePlaceHolder"
                          data-test="travel-letter-date-return"
                        />
                      </Field>
                      <ValidationError
                        name="ReturnDate"
                        :overrides="{ max_date: vueModel.ContentModel.HomecomingValidationText }"
                        data-test="travel-letter-validation-return"
                      />
                    </div>
                  </div>
                </div>
                <div
                  v-for="(traveller, index) in vueModel.TemplateModel.InsuredPersons"
                  :key="'travellerHey-' + traveller.id"
                  class="control push-up"
                  data-test="travel-letter-container-person"
                >
                  <div class="row">
                    <div class="small-8 columns">
                      <h3
                        data-test="travel-letter-label-fullname"
                        v-html="formatSitecoreInputForVue(index)"
                      />
                    </div>
                    <div class="small-4 columns text-right">
                      <button class="minus" @click="removeTraveller(index)" />
                    </div>
                  </div>
                  <Field
                    v-model="traveller.FullName"
                    :rules="'required'"
                    type="text"
                    :placeholder="vueModel.ContentModel.FullNamePlaceholder"
                    :label="vueModel.ContentModel.FullNameFieldName"
                    :name="'FullName-' + traveller.id"
                    data-test="travel-letter-input-fullname"
                  />
                  <ValidationError
                    :name="'FullName-' + traveller.id"
                    data-test="travel-letter-validation-fullname"
                  />
                  <h3
                    data-test="travel-letter-label-cpr-number"
                    v-html="vueModel.ContentModel.CPRNumber"
                  />
                  <div class="row">
                    <div class="large-6 medium-12 columns">
                      <cpr-number-field
                        v-model="traveller.CPRNumber"
                        :name="'cpr-' + traveller.id"
                        :required="true"
                        :cprLabel="vueModel.ContentModel.CPRNumberFieldName"
                      />
                    </div>
                  </div>
                </div>

                <div class="control row push-up">
                  <div class="small-6 small-offset-6 columns">
                    <button
                      class="button secondary"
                      :disabled="vueModel.TemplateModel.InsuredPersons.length === 6"
                      data-test="travel-letter-button-add-person"
                      @click="addTraveller"
                    >
                      <span
                        v-if="vueModel.TemplateModel.InsuredPersons.length > 0"
                        v-html="vueModel.ContentModel.AddEkstraPerson"
                      />
                      <span v-else v-html="vueModel.ContentModel.AddPerson" />
                    </button>
                    <Field
                      v-model="noOfTravellers"
                      :rules="{ min_value: 1 }"
                      type="hidden"
                      name="travellersNo"
                      label="Antal personer"
                    />
                    <ValidationError
                      name="travellersNo"
                      data-test="travel-letter-validation-no-of-travellers"
                    />
                  </div>
                  <div
                    v-if="vueModel.TemplateModel.InsuredPersons.length === 6"
                    class="small-12 columns"
                  >
                    <div
                      class="info-box is-open info-box-align-right"
                      v-html="vueModel.ContentModel.InfoBox"
                    />
                  </div>
                </div>

                <div class="control push-up">
                  <fieldset class="checkbox-inline">
                    <Field
                      id="Confirmation"
                      :rules="'required'"
                      type="checkbox"
                      :value="true"
                      :unchecked-value="false"
                      name="Confirmation"
                      :label="vueModel.ContentModel.ConfirmationFieldName"
                      data-test="travel-letter-input-confirmation"
                    />
                    <label for="Confirmation" v-html="vueModel.ContentModel.Confirmation" />
                  </fieldset>
                  <ValidationError
                    name="Confirmation"
                    data-test="travel-letter-validation-confirmation"
                  />
                </div>

                <div class="control">
                  <p>
                    <a
                      class="round-arrow-with-text"
                      data-collapse="travelLetterConditions"
                      data-test="travel-letter-conditions-arrow-button"
                    >
                      <span class="round-arrow down" />
                      <span
                        data-test="travel-letter-conditions-title"
                        v-html="vueModel.ContentModel.ConditionsTitle"
                      />
                    </a>
                  </p>
                  <div
                    id="travelLetterConditions"
                    class="collapse-target"
                    data-test="travel-letter-conditions-content"
                    v-html="vueModel.ContentModel.Conditions"
                  />
                </div>

                <div class="control row push-up">
                  <div class="small-6 small-offset-6 columns">
                    <button
                      class="button large"
                      data-test="travel-letter-button-submit"
                      @click="submitData"
                      v-html="vueModel.ContentModel.OrderTravelingDocumentsButton"
                    />
                  </div>
                </div>

                <hr />
                <div class="control">
                  <h3
                    data-test="travel-letter-title-other-documents"
                    v-html="vueModel.ContentModel.OtherImportantDocumentsTitle"
                  />
                  <div
                    data-test="travel-letter-description-other-documents"
                    v-html="vueModel.ContentModel.OtherImportantDocumentsDescription"
                  />
                </div>
              </article>
            </section>

            <section v-if="showReceipt" data-test="travel-letter-receipt">
              <h3 v-html="vueModel.ContentModel.ResultModel.Headline" />
              <article class="flow-group">
                <div class="control">
                  <div v-html="vueModel.ContentModel.ResultModel.Description" />
                  <div class="row push-up">
                    <div class="small-12 medium-6 columns">
                      <p>
                        <a
                          :href="vueModel.ContentModel.ResultModel.PrimaryButtonURL"
                          class="button large"
                          v-html="vueModel.ContentModel.ResultModel.PrimaryButtonLabel"
                        />
                      </p>
                    </div>
                  </div>
                </div>
              </article>
            </section>
          </div>
        </div>
        <transition v-cloak name="modal">
          <v-modal
            :is-open="showErrorModal"
            data-test="travel-letter-error-modal"
            @close="showErrorModal = false"
          >
            <template #title><h3>Der skete en fejl</h3></template>
            <template #content>
              <div>
                <p>Prøv igen senere eller kontakt os</p>
              </div>
            </template>
            <template #buttons>
              <button class="button" @click="showErrorModal = false">Luk</button>
            </template>
          </v-modal>
        </transition>
      </div>
    </Form>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import Utility from '@/common/UtilityFunctions.js';
import ValidationError from '@/components/UI/ValidationError.vue';
import DatePicker from '@/components/UI/Pickadate.vue';
import H3AndTooltip from '@/components/UI/H3AndTooltip.vue';
import { Field } from 'vee-validate';
import PageSpinner from '@/components/UI/PageSpinner.vue';
import CprNumberField from '@/components/UI/CprNumberField.vue';
import VueSelect from '@/components/UI/VueSelect.vue';

export default {
  name: 'TravelLetter',
  components: {
    PageSpinner,
    VueSelect,
    Field,
    CprNumberField,
    ValidationError,
    DatePicker,
    H3AndTooltip
  },
  props: {
    data: {
      type: Object,
      default() {
        return {};
      }
    }
  },
  data() {
    return {
      vueModel: this.data,
      countryList: [
        {
          Id: 6,
          Name: 'Canada'
        },
        {
          Id: 0,
          Name: 'Cuba'
        },
        {
          Id: 1,
          Name: 'Iran'
        },
        {
          Id: 5,
          Name: 'Mexico'
        },
        {
          Id: 2,
          Name: 'Russia'
        },
        {
          Id: 4,
          Name: 'South Africa'
        },
        {
          Id: 3,
          Name: 'USA'
        }
      ],
      today: moment().startOf('date'),
      dateFormat: window.dateFormat,
      showReceipt: false,
      showErrorModal: false
    };
  },
  computed: {
    spinnerUrlList() {
      return [Utility.constants().API.travel_letter_create];
    },
    noOfTravellers() {
      return this.vueModel.TemplateModel.InsuredPersons.length;
    },
    initialCprValue() {
      const fullCpr = this.vueModel.TemplateModel.InsuredPersons[0].CPRNumber;
      return {
        birthDay: fullCpr.substring(0, 6),
        cprDigits: fullCpr.substring(6)
      };
    },
    earliestReturnDate() {
      return this.vueModel.TemplateModel.FromDate === null
        ? this.today
        : moment(this.vueModel.TemplateModel.FromDate);
    },
    earliestReturnDateFormatted() {
      return this.earliestReturnDate.format(this.dateFormat);
    },
    latestReturnDate() {
      return this.vueModel.TemplateModel.FromDate === null
        ? this.today
        : moment(this.vueModel.TemplateModel.FromDate).add(60, 'days');
    },
    latestReturnDateFormatted() {
      return this.latestReturnDate.format(this.dateFormat);
    }
  },
  mounted() {
    this.vueModel.TemplateModel.InsuredPersons[0].id = crypto.randomUUID();
  },
  methods: {
    submitData() {
      this.$refs.form.validate().then((result) => {
        if (result.valid) {
          axios
            .post(Utility.constants().API.travel_letter_create, this.vueModel.TemplateModel)
            .then((response) => {
              this.vueModel.ContentModel.ResultModel = response.data.ContentModel.ResultModel;
              this.showReceipt = true;
            })
            .catch(() => {
              this.showErrorModal = true;
            });
        }
      });
    },
    addTraveller() {
      this.vueModel.TemplateModel.InsuredPersons.push({
        id: crypto.randomUUID(),
        CPRNumber: null,
        FullName: null
      });
    },
    removeTraveller(index) {
      this.vueModel.TemplateModel.InsuredPersons.splice(index, 1);
    },
    formatSitecoreInputForVue(index) {
      return this.vueModel.ContentModel.FullName.replace('{{index + 1}}', index + 1);
    }
  }
};
</script>
